import React from 'react';
import { motion } from 'framer-motion';
import { Layers, Shirt, Image as ImageIcon, CreditCard, ArrowRight, Printer, Key } from 'lucide-react';
const primaryServices = [{
  id: 'dtf',
  title: 'Custom DTF Transfers',
  description: 'Fast, high-quality DTF transfers printed in-house and shipped nationwide. Built for speed, consistency, and repeat orders — perfect for teams, brands, and shops.',
  icon: Layers,
  href: '/dtf-printing',
  featured: true
}, {
  id: 'screen-printing',
  title: 'Screen Printing',
  description: 'Bold, durable screen prints for businesses, race teams, events, and organizations. No minimums, no setup fees, and fast turnaround.',
  icon: Printer,
  href: '/screen-printing',
  featured: false
}, {
  id: 'apparel',
  title: 'Custom Apparel',
  description: 'Custom apparel programs for businesses, crews, teams, and events — including business workwear, team apparel, and event shirts designed for consistent branding.',
  icon: Shirt,
  href: '/custom-apparel',
  featured: false
}];
const secondaryServices = [{
  id: 'banners',
  title: 'Banners',
  description: 'Durable indoor and outdoor banners for job sites, events, and promotions.',
  icon: ImageIcon,
  href: '/custom-banners'
}, {
  id: 'business-cards',
  title: 'Business Cards',
  description: 'Clean, professional business cards to support your brand and visibility.',
  icon: CreditCard,
  href: '/business-cards'
}, {
  id: 'key-tags',
  title: 'Custom Key Tags',
  description: "Affordable, high-visibility branding that stays on your customer's keychain.",
  icon: Key,
  href: '/custom-key-tags'
}];
export function ProductServices() {
  return <section className="py-24 bg-atomic-charcoal relative" data-id="element-311">
      <div className="container mx-auto px-4 md:px-6" data-id="element-312">
        <div className="max-w-6xl mx-auto" data-id="element-313">
          {/* Header */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-314">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-315">
              Our Services
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed" data-id="element-316">
              Our core services are built around speed, consistency, and
              reliability — from nationwide DTF printing and shipping to custom
              apparel programs for businesses, teams, and events.
            </p>
          </motion.div>

          {/* Primary Services - Large Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-6" data-id="element-317">
            {primaryServices.map((service, index) => <motion.a key={service.id} href={service.href} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} whileHover={{
            y: -5
          }} className={`group relative bg-atomic-dark rounded-xl overflow-hidden border transition-all ${service.featured ? 'border-atomic-green/50 shadow-lg shadow-atomic-green/10' : 'border-white/10 hover:border-atomic-green/50'}`} data-id="element-318">
                {service.featured && <div className="absolute top-4 right-4 px-3 py-1 bg-atomic-green text-black text-xs font-bold rounded-full" data-id="element-319">
                    POPULAR
                  </div>}

                <div className="p-8 h-full flex flex-col" data-id="element-320">
                  <div className={`mb-6 p-4 w-fit rounded-lg transition-colors ${service.featured ? 'bg-atomic-green/20 text-atomic-green border border-atomic-green/30' : 'bg-atomic-green/10 text-atomic-green border border-atomic-green/20 group-hover:bg-atomic-green group-hover:text-black'}`} data-id="element-321">
                    <service.icon size={32} data-id="element-322" />
                  </div>

                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-323">
                    {service.title}
                  </h3>

                  <p className="text-gray-400 leading-relaxed mb-6 group-hover:text-gray-300 transition-colors flex-grow" data-id="element-324">
                    {service.description}
                  </p>

                  <div className="flex items-center text-atomic-green font-medium group-hover:text-white transition-colors mt-auto" data-id="element-325">
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-326" />
                  </div>
                </div>
              </motion.a>)}
          </div>

          {/* Secondary Services - Smaller Cards */}
          <div className="grid md:grid-cols-3 gap-6" data-id="element-327">
            {secondaryServices.map((service, index) => <motion.a key={service.id} href={service.href} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2 + index * 0.1
          }} whileHover={{
            y: -3
          }} className="group bg-atomic-black rounded-xl p-6 border border-white/10 hover:border-atomic-green/50 transition-all" data-id="element-328">
                <div className="flex items-start gap-4" data-id="element-329">
                  <div className="p-3 bg-atomic-green/10 rounded-lg text-atomic-green border border-atomic-green/20 group-hover:bg-atomic-green group-hover:text-black transition-colors shrink-0" data-id="element-330">
                    <service.icon size={24} data-id="element-331" />
                  </div>

                  <div className="flex-1" data-id="element-332">
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-333">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed group-hover:text-gray-300 transition-colors" data-id="element-334">
                      {service.description}
                    </p>
                  </div>

                  <ArrowRight className="w-5 h-5 text-atomic-green opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" data-id="element-335" />
                </div>
              </motion.a>)}
          </div>
        </div>
      </div>
    </section>;
}